import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { replyNotice, type CompanionNotice } from '../lib/companion.js';
import { useCompanion } from '../state/companion.js';
import { useMeetingRecorder } from '../state/meeting-recorder.js';
import { ROLES, type DocumentKind, type TurnRating, type ConversationRole, type Conversation, type ListenMode, type MemberRole } from '@jabberr/core';
import { GoingOnPanel } from '../components/GoingOnPanel.js';
import { TalkModeControl } from '../components/TalkModeControl.js';
import { ReplyModeNotice } from '../components/ReplyModeNotice.js';
import { BackIcon, MicIcon } from '../components/icons.js';
import { pickableRoles, roleLabel, talkTitleFor } from '../lib/plain.js';
import { Composer, type MentionCandidate } from '../components/Composer.js';
import { WriteMenu, sendDocumentTurn } from '../components/WriteMenu.js';
import { ParkEndCard } from '../components/ParkEndCard.js';
import { wantsToWrapUp } from '../lib/parking.js';
import { conversationActions } from '../lib/conversation-actions.js';
import type { DocumentPrompt } from '@jabberr/core';
import { TurnView, type PinRequest } from '../components/TurnView.js';
import { ErrorBox, Spinner } from '../components/ui.js';
import { api, errorMessage } from '../lib/api.js';
import { notifyBackground } from '../lib/notify.js';
import { conversationTitle } from '../lib/format.js';
import { canReplyAnyway, isContextFull, latestSummary, type ViewTurn } from '../lib/turns.js';
import { initials } from '../lib/format.js';
import { useChat } from '../state/chat.js';
import { useSession } from '../state/session.js';
import { useDrawer } from '../state/drawer.js';

interface FirstTurnState {
  firstTurn?: { clientTurnId: string; content: string; purpose?: DocumentKind };
}

const SUGGESTIONS: Record<string, string[]> = {
  product_owner: ['Pressure-test an idea', 'Turn notes into requirements', 'What are we missing?'],
  open: ['Pressure-test an idea', 'Turn notes into requirements', 'What are we missing?'],
  client_partner: ['Review a stakeholder concern', 'Turn notes into requirements', 'What are we missing?'],
  marketing: ['Pressure-test a launch message', 'Draft a brief for this', 'Who is this actually for?'],
  strategy: ['Lay out the options', 'What decision does this change?', 'Draft a decision memo'],
};

const EMPTY_HEADING: Record<string, string> = {
  product_owner: 'What are we trying to figure out?',
  open: 'What is on your mind?',
  client_partner: 'How can we help?',
  marketing: 'What are we telling people, and to whom?',
  strategy: 'What are we deciding?',
};

export function ConversationPage() {
  const { projectId = '', conversationId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSession();
  const chat = useChat(conversationId, user?.id);
  // Read state lives on the server now: mark the thread seen while the page is open and
  // visible, and again whenever another turn arrives while visible.
  useEffect(() => {
    if (!conversationId) return;
    const mark = (): void => {
      if (document.visibilityState === 'visible') void api.conversations.seen(conversationId).catch(() => undefined);
    };
    mark();
    document.addEventListener('visibilitychange', mark);
    return () => document.removeEventListener('visibilitychange', mark);
  }, [conversationId, chat.turns.length]);
  const drawer = useDrawer();
  const meetings = useMeetingRecorder();
  const [replyAlert, setReplyAlert] = useState<CompanionNotice | null>(null);
  const previousRequest = useRef({ conversationId, running: false });
  useEffect(() => {
    const previous = previousRequest.current;
    if (previous.conversationId !== conversationId || chat.streaming) setReplyAlert(null);
    else if (previous.running && !chat.loading) setReplyAlert(replyNotice([...chat.turns].reverse().find(t => t.kind === 'assistant' || t.kind === 'user')));
    previousRequest.current = { conversationId, running: previous.conversationId === conversationId && chat.streaming };
  }, [conversationId, chat.streaming, chat.loading, chat.turns]);
  // Jabber is app-wide; this page hands him its status and hands back what a click means here.
  const { setNotice, setWaiting, markDone, clearDone } = useCompanion();
  // Opening a thread counts as reading it: its done dot (sidebar and above Jabber) goes away.
  useEffect(() => { clearDone(conversationId); }, [conversationId, clearDone]);
  // A finished reply leaves a done dot until the thread is opened again, even after leaving this page.
  const readyReply = replyAlert?.tone === 'ready' ? replyAlert : null;
  useEffect(() => {
    if (readyReply) {
      markDone({ conversationId, projectId, title: conversationTitle(chat.conversation?.title ?? '') });
      notifyBackground('Jabberr replied', conversationTitle(chat.conversation?.title ?? ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyReply?.id]);
  // After a finished reply, mention anything Jabberr learned since the Memory page was last seen.
  const [memoryNote, setMemoryNote] = useState<number | null>(null);
  useEffect(() => {
    if (!readyReply || !projectId) return;
    const key = `jabberr:memorySeen:${projectId}`;
    let seen: string | null = null;
    try {
      seen = localStorage.getItem(key);
    } catch {
      seen = null;
    }
    api.memory
      .list(projectId)
      .then(({ project, user }) => {
        const fresh = [...project, ...user].filter((i) => i.createdAt > (seen ?? '')).length;
        if (fresh > 0) setMemoryNote(fresh);
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyReply?.id]);
  const notice: CompanionNotice | null = meetings.notice ?? replyAlert;
  useEffect(() => {
    setNotice(notice ? {
      ...notice,
      detail: replyAlert ? conversationTitle(chat.conversation?.title ?? '') : notice.detail,
      onAcknowledge: () => {
        if (meetings.notice) { const target = meetings.notice; meetings.acknowledge(); if (target.href) navigate(target.href); else meetings.reveal(); }
        else { setReplyAlert(null); clearDone(conversationId); stickToBottom.current = true; scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'instant' }); scrollRef.current?.focus(); }
      },
    } : null);
    return () => setNotice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notice]);
  const waiting = chat.streaming && chat.turns.some((t) => t.kind === 'assistant' && t.status === 'streaming' && !t.text);
  useEffect(() => {
    setWaiting(waiting);
    return () => setWaiting(false);
  }, [waiting, setWaiting]);
  const [error, setError] = useState<string | null>(null);
  // Suggestions include the currently admitted guest who owns this client conversation.
  const [mentionCandidates, setMentionCandidates] = useState<MentionCandidate[] | undefined>(undefined);
  useEffect(() => {
    setMentionCandidates(undefined);
    api.conversations
      .mentionCandidates(conversationId)
      .then(({ candidates }) => setMentionCandidates(candidates))
      .catch(() => setMentionCandidates(undefined));
  }, [conversationId]);
  const [pinned, setPinned] = useState<{ heading: string; version: number } | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [listenBusy, setListenBusy] = useState(false);
  // The project's name for the header, and the caller's role in it: a guest gets no role menu and no talk.
  const [projectName, setProjectName] = useState<string | null>(null);
  const [memberRole, setMemberRole] = useState<MemberRole | undefined>(undefined);
  const [projectKind, setProjectKind] = useState<string | null>(null);
  const [salesEnabled, setSalesEnabled] = useState(false);
  const [switching, setSwitching] = useState(false);
  useEffect(() => {
    let alive = true;
    api.projects
      .get(projectId)
      .then((r) => {
        if (!alive) return;
        setProjectName(r.project.name);
        setProjectKind(r.project.kind);
        setMemberRole(r.role);
      })
      .catch(() => undefined);
    api
      .authConfig()
      .then((config) => alive && setSalesEnabled(config.meetingPersonasEnabled === true))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [projectId]);
  const guest = memberRole === 'guest';
  // Talks are meetings, which the api refuses to guests and in a personal space.
  const canTalk = memberRole !== undefined && !guest && projectKind !== null && projectKind !== 'personal';
  const roleChoices = pickableRoles(salesEnabled).filter(role => !chat.conversation || ROLES[role].audience === ROLES[chat.conversation.role].audience);
  // Empty state draft: the text shown in suggestion chips.
  const [draft, setDraft] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const firstTurnSent = useRef<string | null>(null);
  const pendingFirstTurn = useRef<FirstTurnState['firstTurn'] | null>(null);
  const documentCache = useRef<{ docs: DocumentPrompt[] | null }>({ docs: null });
  // "What should happen with this?" (OPEN-38): after Jabberr's reply when the person wraps up, or from Write...
  // (Add to parking lot, which starts the note at once). Kept once shown, so the note's own turns do not hide it.
  const [endCard, setEndCard] = useState<{ key: string; autoPark: boolean } | null>(null);
  // New idea by talking (?idea=1): a bar offers Write the note once there has been some back and forth.
  const ideaMode = new URLSearchParams(location.search).get('idea') === '1';
  const dismissedEnd = useRef(new Set<string>());

  // A review conversation arrives with its seeded first turn in history state. Capture it once and
  // clear the state right away so a refresh does not resend it.
  const incoming = (location.state as FirstTurnState | null)?.firstTurn;
  if (incoming && firstTurnSent.current !== incoming.clientTurnId) pendingFirstTurn.current = incoming;
  useEffect(() => {
    if (incoming) navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoming]);

  // Send it once history has loaded (so a StrictMode remount or a slow load cannot abort it), unless
  // the server already has a turn with that clientTurnId.
  useEffect(() => {
    if (chat.loading) return;
    const ft = pendingFirstTurn.current;
    if (!ft || firstTurnSent.current === ft.clientTurnId) return;
    firstTurnSent.current = ft.clientTurnId;
    pendingFirstTurn.current = null;
    if (chat.turns.some((t) => t.clientTurnId === ft.clientTurnId)) return;
    void chat.send(ft.content, ft.clientTurnId, ft.purpose);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.loading]);

  useEffect(() => {
    const c = chat.conversation;
    const title = c ? (c.title ? c.title.slice(0, 60) : '') : '';
    const onDetails = (): void => {
      drawer.setOpen(!drawer.open);
    };
    const onTalk = (): void => {
      void talkInstead();
    };
    const onWrite = (kind: DocumentKind): void => {
      setError(null);
      sendDocumentTurn(kind, chat.send, documentCache.current).catch((err) => setError(errorMessage(err)));
    };
    const onPark = (): void => setEndCard({ key: `write-${Date.now()}`, autoPark: true });
    drawer.setInfo(
      c
        ? { projectId, contextVersion: c.contextVersion, reasoningEffort: c.reasoningEffort, conversationKind: c.kind, role: c.role, title: conversationTitle(title, ''),
            actions: conversationActions({ conversation: c, streaming: chat.streaming, memberRole, projectKind, onWrite, ...(c.kind === 'chat' ? { onPark } : {}), onTalk, onDetails }) }
        : { projectId, title },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.conversation, chat.streaming, chat.send, projectId, memberRole, projectKind]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottom.current && chat.turns.at(-1)?.kind !== 'system') el.scrollTop = el.scrollHeight;
  }, [chat.turns]);

  // A search hit for a specific reply: scroll it into view and flash it once history is there.
  const [searchParams, setSearchParams] = useSearchParams();
  const highlight = searchParams.get('highlight');
  const [flash, setFlash] = useState<string | null>(null);
  useEffect(() => {
    if (!highlight || chat.loading) return;
    if (!chat.turns.some((t) => t.id === highlight)) return;
    const remaining = new URLSearchParams(searchParams);
    remaining.delete('highlight');
    setSearchParams(remaining, { replace: true });
    setFlash(highlight);
    const el = scrollRef.current?.querySelector(`[data-turn-key="${highlight}"]`);
    el?.scrollIntoView({ block: 'center' });
    stickToBottom.current = false;
    const timer = setTimeout(() => setFlash(null), 2500);
    return () => clearTimeout(timer);
  }, [highlight, chat.loading, chat.turns, setSearchParams]);

  const onScroll = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const selectSuggestion = (text: string): void => {
    setDraft(text);
    requestAnimationFrame(() => {
      const textarea = document.querySelector('textarea[aria-label="Message"]') as HTMLTextAreaElement | null;
      textarea?.focus();
    });
  };

  // Publishes the bullet as the next context version. This conversation keeps its pinned version; new ones pick it up.
  const pin = async (turn: ViewTurn, req: PinRequest): Promise<void> => {
    if (!turn.id) return;
    setError(null);
    try {
      const { version } = await api.context.pin(projectId, { turnId: turn.id, heading: req.heading, text: req.text });
      setPinned({ heading: req.heading, version: version.version });
    } catch (err) {
      setError(errorMessage(err));
      throw err;
    }
  };

  const saveTitle = async (): Promise<void> => {
    setEditingTitle(false);
    const title = titleDraft.trim();
    if (!chat.conversation || title === (chat.conversation.title ?? '')) return;
    try {
      const { conversation } = await api.conversations.update(conversationId, { title });
      chat.setConversation(conversation);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const c = chat.conversation;
  const full = isContextFull(chat.turns);
  const summary = latestSummary(chat.turns);
  const usage = chat.contextUsage;
  const percent = usage ? Math.round(usage.usedFraction * 100) : 0;
  const empty = !chat.loading && chat.turns.length === 0 && !chat.loadError;
  const conversationRole = c?.role ?? 'product_owner';

  const save = async (turn: ViewTurn, saved: boolean): Promise<void> => {
    if (!turn.id) return;
    try {
      await api.outputs.saveTurn(conversationId, turn.id, { saved });
      await chat.reload();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const rate = async (turn: ViewTurn, rating: TurnRating | null): Promise<void> => {
    if (!turn.id) return;
    try {
      await api.outputs.rateTurn(conversationId, turn.id, { rating });
      await chat.reload();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const startContinuation = async (): Promise<void> => {
    if (!c) return;
    try {
      const { conversation } = await api.conversations.create(projectId, {
        title: `${conversationTitle(c.title)} (continued)`.slice(0, 200),
        reasoningEffort: c.reasoningEffort,
        role: c.role,
      });
      navigate(`/p/${projectId}/c/${conversation.id}${new URLSearchParams(location.search).get('embed') === '1' ? '?embed=1' : ''}`);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  /** A switch keeps this page, composer and history, while the server pins the new role prompt. */
  const switchRole = async (role: ConversationRole): Promise<void> => {
    if (!c || role === c.role || switching || chat.streaming) return;
    const top = scrollRef.current?.scrollTop;
    const sticky = stickToBottom.current;
    stickToBottom.current = false;
    setSwitching(true);
    try {
      const { conversation } = await api.conversations.update(c.id, { role, expectedRole: c.role });
      chat.setConversation(conversation);
      await chat.reload();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      requestAnimationFrame(() => {
        if (scrollRef.current && top !== undefined) scrollRef.current.scrollTop = top;
        stickToBottom.current = sticky;
      });
      setSwitching(false);
    }
  };

  /** Talk instead: a meeting for you and Jabberr about this conversation's subject, where he answers everything said. */
  const talkInstead = async (): Promise<void> => {
    if (switching) return;
    setSwitching(true);
    try {
      const { meeting } = await api.meetings.create(projectId, { title: talkTitleFor(conversationTitle(c?.title, '')), listenMode: 'always' });
      navigate(`/meet/${meeting.id}`, { state: { inviteJabberr: true } });
    } catch (err) {
      setError(errorMessage(err));
      setSwitching(false);
    }
  };

  const setListenMode = (mode: ListenMode): void => {
    if (!c) return;
    setListenBusy(true);
    api.conversations
      .update(c.id, { listenMode: mode })
      .then(({ conversation }) => chat.setConversation(conversation))
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setListenBusy(false));
  };

  const assistantTurns = chat.turns.filter(t => t.kind === 'assistant');
  // The person said they are done in a product owner conversation: offer the choice once, after Jabberr's reply.
  const wrapKey = c && c.status === 'active' && c.kind === 'chat' && c.role === 'product_owner' && !guest && wantsToWrapUp(chat.turns) ? chat.turns.at(-1)?.key ?? null : null;
  useEffect(() => {
    if (wrapKey && !dismissedEnd.current.has(wrapKey)) setEndCard((cur) => cur ?? { key: wrapKey, autoPark: false });
  }, [wrapKey]);
  const emptySuggestions = empty ? (SUGGESTIONS[c?.role ?? 'product_owner'] ?? SUGGESTIONS.product_owner) ?? [] : [];

  return (
    <div className="chat">
      <header className="chat-head">
        <div className="head-left">
          <Link to={`/p/${projectId}`} className="icon-btn chat-back" aria-label={`Back to ${projectName ?? 'the project'}`} title={`Back to ${projectName ?? 'the project'}`}>
            <BackIcon size={18} />
          </Link>
          <div className="chat-title">
            {projectName ? <span className="chat-project">{projectName}</span> : null}
            {editingTitle ? (
              <input
                autoFocus
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={() => void saveTitle()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void saveTitle();
                  if (e.key === 'Escape') setEditingTitle(false);
                }}
                aria-label="Conversation title"
                maxLength={200}
              />
            ) : (
              <button
                type="button"
                className="title-btn"
                onClick={() => {
                  setTitleDraft(c?.title ?? '');
                  setEditingTitle(true);
                }}
                title="Rename"
              >
                {c ? conversationTitle(c.title) : '...'}
              </button>
            )}
          </div>
        </div>
        {c ? (
          <div className="head-actions">
            <div className="presence-row" aria-label="Who is here now">
              {chat.presence
                .filter((m) => m.userId !== user?.id)
                .map((m) => (
                  <span key={m.userId} className="presence-chip" title={`${m.name ?? 'Someone'} is here`}>
                    <span className="avatar" aria-hidden="true">
                      {initials(m.name ?? '?')}
                    </span>
                  </span>
                ))}
            </div>
            {guest ? null : (
              <label className="role-pick" title="Switch Jabberr’s role in this conversation">
                <span className="role-pick-label">Jabberr as</span>
                <select value={c.role} onChange={(e) => void switchRole(e.target.value as ConversationRole)} aria-label="Jabberr as" disabled={switching || chat.streaming}>
                  {(roleChoices.includes(c.role) ? roleChoices : [c.role, ...roleChoices]).map((r) => (
                    <option key={r} value={r}>
                      {roleLabel(r)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {c.status === 'active' ? (
              <WriteMenu
                disabled={chat.streaming || c.lastTurnSeq === 0}
                send={chat.send}
                onError={setError}
                {...(c.kind === 'chat' ? { onPark: () => setEndCard({ key: `write-${Date.now()}`, autoPark: true }) } : {})}
              />
            ) : null}
            {!canTalk ? null : (
              <button type="button" className="btn btn-primary talk-btn" onClick={() => void talkInstead()} disabled={switching}>
                <MicIcon size={16} />
                Talk instead
              </button>
            )}
            <button
              type="button"
              className={`icon-btn details-toggle ${drawer.open ? 'active' : ''}`}
              aria-label={drawer.open ? 'Hide details' : 'Show details'}
              aria-pressed={drawer.open}
              title={drawer.open ? 'Hide details' : 'Details: model, role, project notes version'}
              onClick={() => drawer.setOpen(!drawer.open)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4M12 8h.01" />
              </svg>
            </button>
          </div>
        ) : null}
      </header>

      {usage && percent >= 70 && !full ? (
        <div className="context-banner" role="status">
          This conversation is {Math.min(percent, 100)}% full. Older exchanges will be summarised as it fills, so add anything you must keep to the
          project notes.
        </div>
      ) : null}

      <div className="chat-body">
        <div className="chat-col">
          {c && c.role !== 'open' && memberRole !== undefined && !guest ? (
            <div className={`chat-talk-bar ${drawer.open ? 'show' : ''}`}>
              <span className="going-on-label">When Jabberr replies</span>
              <TalkModeControl value={c.listenMode} onChange={setListenMode} busy={listenBusy} />
            </div>
          ) : null}
      <div className="chat-stage">
        <div className="chat-scroll" tabIndex={-1} ref={scrollRef} onScroll={onScroll}>
          {error ? <ErrorBox>{error}</ErrorBox> : null}
          {pinned ? (
            <div className="pinned-note" role="status">
              Added under {pinned.heading} in the project notes (version {pinned.version}). New conversations start from it.{' '}
              <Link to={`/p/${projectId}/knows`}>View</Link>{' '}
              <button type="button" className="link-btn" onClick={() => setPinned(null)}>
                dismiss
              </button>
            </div>
          ) : null}
          {memoryNote ? (
            <div className="pinned-note" role="status">
              Jabberr picked up {memoryNote} new {memoryNote === 1 ? 'note' : 'notes'}.{' '}
              <Link to={`/p/${projectId}/knows`}>Review them</Link>{' '}
              <button type="button" className="link-btn" onClick={() => setMemoryNote(null)}>
                dismiss
              </button>
            </div>
          ) : null}
          {chat.loadError ? (
            <ErrorBox>
              Could not load history: {chat.loadError}{' '}
              <button type="button" className="link-btn" onClick={() => void chat.reload()}>
                retry
              </button>
            </ErrorBox>
          ) : null}
          {chat.loading && chat.turns.length === 0 ? <Spinner label="Loading history" /> : null}
          {empty ? (
            <div className="empty chat-empty">
              <img className="empty-otter" src="/apple-touch-icon.png" alt="" width="96" height="96" aria-hidden="true" />
              <h2 className="empty-heading">{EMPTY_HEADING[c?.role ?? 'product_owner'] ?? EMPTY_HEADING.product_owner}</h2>
              <p className="empty-subtitle">I already know this project&apos;s context. Start anywhere.</p>
              <div className="suggestion-row">
                {emptySuggestions.map((text) => (
                  <button
                    key={text}
                    type="button"
                    className="chip suggestion-chip"
                    onClick={() => selectSuggestion(text)}
                  >
                    {text}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          <div className="turns">
            {summary ? (
              <details className="summary-row">
                <summary className="muted small">Earlier in this conversation</summary>
                <pre className="summary-body">{summary.text}</pre>
              </details>
            ) : null}
            {chat.turns.map((t, i) => {
              // Keen stayed silent on this turn and no reply followed: offer the override. The badge
              // is also how overrides become labelled examples for the decision classifier.
              const replyAnyway = canReplyAnyway(t, chat.turns.slice(i + 1)) && c ? (turn: ViewTurn) => {
                api.conversations.regenerate(c.id, turn.id!).catch((err) => setError(errorMessage(err)));
              } : undefined;
              const assistantIdx = t.kind === 'assistant' ? assistantTurns.findIndex(at => at.key === t.key) : -1;
              return (
                <TurnView
                  key={t.key}
                  turn={t}
                  highlighted={flash === t.id}
                  onRetry={(turn) => void chat.retry(turn)}
                  onPin={pin}
                  canPin={t.kind === 'assistant' && t.status === 'completed'}
                  onSave={save}
                  onRate={rate}
                  outputHref={(turn) => `/p/${projectId}/outputs/${turn.id}`}
                  onReplyAnyway={replyAnyway}
                  role={c?.role}
                  assistantIndex={assistantIdx}
                  turnIndex={i}
                />
              );
            })}
          </div>
          {endCard && c ? (
            <ParkEndCard
              key={endCard.key}
              conversationId={c.id}
              autoPark={endCard.autoPark}
              canDraft={!guest}
              onDraftRequirements={() => {
                dismissedEnd.current.add(endCard.key);
                setEndCard(null);
                setError(null);
                sendDocumentTurn('requirements', chat.send, documentCache.current).catch((err) => setError(errorMessage(err)));
              }}
              onParked={() => void chat.reload()}
              onDismiss={() => {
                dismissedEnd.current.add(endCard.key);
                setEndCard(null);
              }}
            />
          ) : null}
        </div>
      </div>

      {ideaMode && c && c.status === 'active' && !endCard && c.lastTurnSeq >= 4 && !chat.streaming ? (
        <div className="idea-bar" role="status">
          <span>When you are ready, Jabberr writes the note for the parking lot. You check it there.</span>
          <button type="button" className="btn btn-primary" onClick={() => setEndCard({ key: `idea-${Date.now()}`, autoPark: true })}>
            Write the note
          </button>
        </div>
      ) : null}
      {c && <ReplyModeNotice kind={c.kind} listenMode={c.listenMode} projectKind={projectKind} />}
      {full ? (
        <div className="chat-full">
          <div>
            <strong>This conversation is full.</strong> Jabberr cannot hold the project notes and the latest exchange at once. Start a new
            conversation to continue; add what matters to the project notes first.
          </div>
          <button type="button" className="btn btn-primary" onClick={() => void startContinuation()}>
            Start a new conversation
          </button>
        </div>
      ) : (
        <Composer
          onSend={(content) => chat.send(content)}
          onStop={chat.stop}
          streaming={chat.streaming}
          mentionCandidates={mentionCandidates}
          placeholder={c?.role === 'open' ? 'Message Jabberr' : 'Message Jabberr'}
          autoFocus
          seedText={draft ?? undefined}
        />
      )}
        </div>
        {c && memberRole !== undefined && !drawer.open ? (
          <GoingOnPanel conversation={c} projectId={projectId} guest={guest} listenBusy={listenBusy} onListenMode={setListenMode} />
        ) : null}
      </div>
    </div>
  );
}
